import { IPurchasingRepository, PurchaseOrderItemStatus } from '@domain/purchasing';
import { PurchaseOrder } from '@domain/purchasing';
import { PurchaseOrderItem } from '@domain/purchasing/models/PurchaseOrderItem';
import { PurchaseOrderRecommendation } from '@domain/purchasing/models/PurchaseOrderRecommendation';
import { Shipment } from '@domain/shipment';
import { Resource } from '@domain/base/Resource';
import { ApiError } from '@domain/base/ApiError';
import { PurchasingDataSource } from '../datasources/PurchasingDataSource';
import { PurchaseOrderMapper } from '../mappers/PurchaseOrderMapper';
import { PurchaseOrderItemMapper } from '../mappers/PurchaseOrderItemMapper';
import { PurchaseOrderRecommendationMapper } from '../mappers/PurchaseOrderRecommendationMapper';
import { ShipmentMapper } from '../mappers/ShipmentMapper';

export class PurchasingRepository implements IPurchasingRepository {
  constructor(private dataSource: PurchasingDataSource) { }

  async getPurchaseOrders(filters?: { draft?: boolean }): Promise<PurchaseOrder[]> {
    try {
      const orders = await this.dataSource.getPurchaseOrders(filters);
      return orders.map(PurchaseOrderMapper.toModel);
    } catch (error) {
      throw ApiError.fromCaught(error, 'Failed to get purchase orders');
    }
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    try {
      const order = await this.dataSource.getPurchaseOrder(id);
      return PurchaseOrderMapper.toModel(order);
    } catch (error) {
      throw ApiError.fromCaught(error, 'Failed to get purchase order');
    }
  }

  async createPurchaseOrder(order: Omit<PurchaseOrder, 'id' | 'createdAt' | 'updatedAt'>): Promise<PurchaseOrder> {
    try {
      const dto = PurchaseOrderMapper.toCreateDTO(order as PurchaseOrder);
      const createdOrder = await this.dataSource.createPurchaseOrder(dto);
      return PurchaseOrderMapper.toModel(createdOrder);
    } catch (error) {
      throw ApiError.fromCaught(error, 'Failed to create purchase order');
    }
  }

  async updatePurchaseOrder(id: string, order: Partial<PurchaseOrder>): Promise<Resource<PurchaseOrder>> {
    try {
      const dto = PurchaseOrderMapper.toUpdateDTO(order as PurchaseOrder);
      const updatedOrder = await this.dataSource.updatePurchaseOrder(id, dto);
      return {
        status: 'success',
        data: PurchaseOrderMapper.toModel(updatedOrder)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update purchase order' }
      };
    }
  }

  async publishPurchaseOrder(id: string): Promise<Resource<PurchaseOrder>> {
    try {
      const publishedOrder = await this.dataSource.publishPurchaseOrder(id);
      return {
        status: 'success',
        data: PurchaseOrderMapper.toModel(publishedOrder)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to publish purchase order' }
      };
    }
  }

  async getPurchaseOrderPdf(id: string): Promise<Resource<Blob>> {
    try {
      const blob = await this.dataSource.getPurchaseOrderPdf(id);
      return {
        status: 'success',
        data: blob
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to download purchase order PDF' }
      };
    }
  }

  async deletePurchaseOrder(id: string): Promise<Resource<void>> {
    try {
      await this.dataSource.deletePurchaseOrder(id);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete purchase order' }
      };
    }
  }

  async updatePurchaseOrderItem(itemId: string, updates: { quantity?: number; productionStartDate?: Date; productionReadyDate?: Date, shipmentId?: string, eta?: Date }): Promise<Resource<PurchaseOrderItem>> {
    try {
      const result = await this.dataSource.updatePurchaseOrderItem(itemId, updates);
      return {
        status: 'success',
        data: PurchaseOrderItemMapper.toModel(result)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update purchase order item' }
      };
    }
  }

  async deletePurchaseOrderItem(purchaseOrderId: string, itemId: string): Promise<Resource<PurchaseOrder>> {
    try {
      const result = await this.dataSource.deletePurchaseOrderItem(purchaseOrderId, itemId);
      return {
        status: 'success',
        data: PurchaseOrderMapper.toModel(result)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete purchase order item' }
      };
    }
  }

  async getPurchaseOrderItemById(id: string): Promise<Resource<PurchaseOrderItem>> {
    try {
      const item = await this.dataSource.getPurchaseOrderItemById(id);
      return {
        status: 'success',
        data: PurchaseOrderItemMapper.toModel(item)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get purchase order item' }
      };
    }
  }

  async getPurchaseOrderItems(filters?: {
    productId?: string;
    purchaseOrderId?: string;
    shipmentId?: string;
    supplierId?: string;
    availableDaysAgo?: number;
  }): Promise<PurchaseOrderItem[]> {
    try {
      const items = await this.dataSource.getPurchaseOrderItems(filters);
      return items.map(PurchaseOrderItemMapper.toModel);
    } catch (error) {
      throw ApiError.fromCaught(error, 'Failed to get purchase order items');
    }
  }

  async calculateAmounts(purchaseOrderId: string): Promise<Resource<PurchaseOrderRecommendation[]>> {
    try {
      const result = await this.dataSource.calculateAmounts(purchaseOrderId);
      const recommendations = result.map(dto => PurchaseOrderRecommendationMapper.toModel(dto));
      return {
        status: 'success',
        data: recommendations
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to calculate amounts' }
      };
    }
  }

  async getActiveShipments(): Promise<Resource<Shipment[]>> {
    try {
      const result = await this.dataSource.getActiveShipments();
      return {
        status: 'success',
        data: result.map(shipment => ShipmentMapper.toModel(shipment))
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get active shipments' }
      };
    }
  }

  async updatePurchaseOrderItemStatus(itemId: string, status: PurchaseOrderItemStatus): Promise<Resource<PurchaseOrderItem>> {
    try {
      const updatedItem = await this.dataSource.updatePurchaseOrderItemStatus(itemId, status);
      return {
        status: 'success',
        data: PurchaseOrderItemMapper.toModel(updatedItem)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update purchase order item status' }
      };
    }
  }

  async updatePurchaseOrderItemSort(itemId: string, sort: number): Promise<Resource<PurchaseOrderItem>> {
    try {
      const updatedItem = await this.dataSource.updatePurchaseOrderItemSort(itemId, sort);
      return {
        status: 'success',
        data: PurchaseOrderItemMapper.toModel(updatedItem)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update purchase order item sort order' }
      };
    }
  }

  async createPurchaseOrderItem(purchaseOrderId: string, item: Omit<PurchaseOrderItem, 'id'>): Promise<Resource<PurchaseOrderItem>> {
    try {
      const dto = PurchaseOrderItemMapper.toCreateDTO(item);
      const createdItem = await this.dataSource.createPurchaseOrderItem(purchaseOrderId, dto);
      return {
        status: 'success',
        data: PurchaseOrderItemMapper.toModel(createdItem)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to create purchase order item' }
      };
    }
  }
} 